import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { NavGroup, NavItem } from '@models/nav-item';
import { filter, map } from 'rxjs';

import { DAY_CONTROLS_ROLE } from '@core/models/day-controls';
import { DIAGNOSTICS_ROLE } from '@core/models/diagnostics';
import { MAS_ROLE } from '@core/models/mas-history';
import { AuthService } from '@core/services/auth';
import { DAY_CONTROLS_ENABLED } from '@core/services/day-controls';
import { DIAGNOSTICS_API_URL } from '@core/services/diagnostics';
import { MAS_API_URL } from '@core/services/mas-history';

/** An item and the keys of the items above it, as the tree lays them out. */
interface NavNode {
  chain: string[];
  route?: string;
}

/** The key an item has in the tree: its parent's key, then its own label. */
function keyOf(parentKey: string, item: NavItem): string {
  return `${parentKey}/${item.label}`;
}

function flatten(items: NavItem[], parents: string[]): NavNode[] {
  return items.flatMap((item) => {
    const chain = [...parents, keyOf(parents[parents.length - 1], item)];
    return [{ chain, route: item.route }, ...flatten(item.children ?? [], chain)];
  });
}

function sameChain(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((key, index) => key === b[index]);
}

function pathOf(url: string): string {
  return url.split(/[?#]/, 1)[0];
}

function onPath(path: string, route: string): boolean {
  return path === route || path.startsWith(`${route}/`);
}

@Component({
  selector: 'yt-sidebar',
  imports: [RouterModule, NgTemplateOutlet],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss'
})
export class Sidebar {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly diagnosticsAvailable = inject(DIAGNOSTICS_API_URL) !== null;
  private readonly masAvailable = inject(MAS_API_URL) !== null;
  private readonly dayControlsAvailable = inject(DAY_CONTROLS_ENABLED);

  readonly expandedGroups = signal<Set<string>>(new Set(['Order Management', 'Entitlements']));

  readonly navGroups: NavGroup[] = [
    {
      label: 'Order Management',
      items: [
        { label: 'Orders', icon: 'pi pi-shopping-cart', route: '/orders' },
        { label: 'Payments', icon: 'pi pi-credit-card', route: '/payments' },
        { label: 'Configuration', icon: 'pi pi-cog', route: '/orders/configuration' }
      ]
    },
    {
      label: 'Menu Management',
      items: [
        { label: 'Menu Items', icon: 'pi pi-list', route: '/menu-items' },
        { label: 'Ingredients', icon: 'pi pi-objects-column', route: '/ingredients' }
      ]
    },
    {
      label: 'Nourishment',
      items: [{ label: 'Recommendation Simulator', icon: 'pi pi-sliders-v', route: '/recommendation-simulator' }]
    },
    {
      label: 'HGK Management',
      items: [
        { label: 'Healthy Gourmet Kitchens', icon: 'pi pi-warehouse', route: '/producers' },
        { label: 'Fulfillment Window Settings', icon: 'pi pi-clock', route: '/producers/fulfillment-window-settings' },
        { label: 'Pricing Floors', icon: 'pi pi-percentage', route: '/producers/pricing-floors' },
        { label: 'Add-On Settings', icon: 'pi pi-plus-circle', route: '/producers/add-on-settings' },
        { label: 'Add-On Stock', icon: 'pi pi-box', route: '/producers/add-on-stock' },
        { label: 'Entree Inventory', icon: 'pi pi-calendar-clock', route: '/producers/entree-inventory' },
        { label: 'Aggregation Facilities', icon: 'pi pi-building', route: '/producers/aggregation-facilities' }
      ]
    },
    {
      label: 'Membership',
      items: [
        { label: 'Packages', icon: 'pi pi-box', route: '/membership/packages' },
        { label: 'Options', icon: 'pi pi-sliders-h', route: '/membership/options' },
        { label: 'Features', icon: 'pi pi-star', route: '/membership/features' },
        { label: 'Reports', icon: 'pi pi-chart-bar', route: '/membership/reports' }
      ]
    },
    {
      label: 'Entitlements',
      items: [{ label: 'Usage Ledger', icon: 'pi pi-book', route: '/entitlements' }]
    },
    {
      label: 'Notifications',
      items: [{ label: 'Notification Rules', icon: 'pi pi-bell', route: '/notification-rules' }]
    },
    {
      label: 'Access Management',
      items: [
        { label: 'Accounts & Users', icon: 'pi pi-users', route: '/access-management/accounts-users' },
        { label: 'Groups', icon: 'pi pi-sitemap', route: '/access-management/groups' },
        { label: 'Invitations', icon: 'pi pi-envelope', route: '/access-management/invitations' },
        { label: 'Reports', icon: 'pi pi-chart-bar', route: '/access-management/reports' },
        { label: 'Configuration', icon: 'pi pi-cog', route: '/access-management/configuration' }
      ]
    },
    {
      label: 'Platform',
      items: [
        ...(this.diagnosticsAvailable
          ? [{ label: 'Diagnostics', icon: 'pi pi-database', route: '/diagnostics', requiredRole: DIAGNOSTICS_ROLE }]
          : []),
        ...(this.masAvailable
          ? [
              {
                label: 'Multi-Agent System',
                icon: 'pi pi-comments',
                requiredRole: MAS_ROLE,
                testId: 'nav-agent-messages',
                children: [
                  {
                    label: 'Messages',
                    icon: 'pi pi-inbox',
                    route: '/agent-messages',
                    testId: 'nav-agent-messages-list'
                  },
                  { label: 'MAS projects', route: '/agent-messages/team/projects', testId: 'nav-team-projects' },
                  { label: 'Workstreams', route: '/agent-messages/team/work', testId: 'nav-team-work' },
                  { label: 'Team actions', route: '/agent-messages/team/actions', testId: 'nav-team-actions' },
                  {
                    label: 'Configuration',
                    route: '/agent-messages/team/configuration',
                    testId: 'nav-team-configuration'
                  },
                  {
                    label: 'Managed Conversations',
                    icon: 'pi pi-comment',
                    route: '/agent-messages/conversations',
                    testId: 'nav-conversations'
                  },
                  {
                    label: 'Coordination Board',
                    icon: 'pi pi-clipboard',
                    route: '/agent-messages/coordination',
                    testId: 'nav-coordination-board'
                  },
                  {
                    label: 'Slack',
                    icon: 'pi pi-slack',
                    route: '/agent-messages/slack',
                    testId: 'nav-slack'
                  }
                ]
              }
            ]
          : []),
        ...(this.dayControlsAvailable
          ? [
              {
                label: 'Day Controls',
                icon: 'pi pi-sliders-h',
                route: '/day-controls',
                requiredRole: DAY_CONTROLS_ROLE
              }
            ]
          : [])
      ]
    }
  ];

  /** The groups this user sees: an item needing a role their token lacks is dropped, and so is a group left empty. */
  readonly visibleNavGroups = computed(() => {
    const roles = this.auth.roles();
    return this.navGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => !item.requiredRole || roles.includes(item.requiredRole))
      }))
      .filter((group) => group.items.length > 0);
  });

  readonly topLevelItems = [
    { label: 'Dashboard', icon: 'pi pi-home', route: '/dashboard' },
    { label: 'Geofences', icon: 'pi pi-map', route: '/geofences' }
  ];

  /** Where the page is without its query string, so a sort or a search does not count as moving within the tree. */
  private readonly path = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => pathOf(this.router.url))
    ),
    { initialValue: pathOf(this.router.url) }
  );

  /**
   * The key of the item the page belongs to and of every item above it, deepest last. Of the items whose route
   * contains the page the longest wins, so a page belongs to its own entry and not to the broader one beside it.
   * A chain equal to the last one is not a change, so a branch the user closed stays closed until the page moves.
   */
  private readonly activeChain = computed<string[]>(
    () => {
      const path = this.path();
      const nodes = this.visibleNavGroups().flatMap((group) => flatten(group.items, [group.label]));
      const best = nodes
        .filter((node) => node.route && onPath(path, node.route))
        .reduce<NavNode | null>(
          (longest, node) => (!longest || node.route!.length > longest.route!.length ? node : longest),
          null
        );
      return best?.chain ?? [];
    },
    { equal: sameChain }
  );

  constructor() {
    // Landing on a page, by link or by navigation, opens the groups and branches that lead to it.
    effect(() => {
      const chain = this.activeChain();
      untracked(() =>
        this.expandedGroups.update((open) =>
          chain.every((key) => open.has(key)) ? open : new Set([...open, ...chain])
        )
      );
    });
  }

  /** The key an item has in the tree: its group's label, then the labels above it, then its own. */
  itemKey(parentKey: string, item: NavItem): string {
    return keyOf(parentKey, item);
  }

  /** Whether the page belongs to this item, which is the one the tree marks as the current page. */
  isCurrent(key: string): boolean {
    return this.activeChain().at(-1) === key;
  }

  /** Whether the page sits at or below this item. */
  isOnActivePath(key: string): boolean {
    return this.activeChain().includes(key);
  }

  /** Opens or closes a group by its label, or an item with children by its key. */
  toggleGroup(key: string): void {
    this.expandedGroups.update((groups) => {
      const next = new Set(groups);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  isGroupExpanded(key: string): boolean {
    return this.expandedGroups().has(key);
  }

  /** Whether the page is at or below this route, read from the page signal so a top-level item follows navigation. */
  isActive(route: string): boolean {
    return onPath(this.path(), route);
  }
}
